import React from 'react';
import { motion } from 'framer-motion';
import { Facebook, Twitter, Linkedin, Instagram } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: 'Home', id: 'hero', type: 'scroll' },
    { name: 'About', id: '/about', type: 'link' },
    { name: 'Services', id: 'services', type: 'scroll' },
    { name: 'Products', id: '/products', type: 'link' },
    { name: 'Our Expertise', id: 'our-expertise', type: 'scroll' },
    { name: 'Contact', id: '/contact', type: 'link' },
  ];
  const socialLinks = [
    { icon: Facebook, href: 'https://facebook.com', label: 'Facebook' },
    { icon: Twitter, href: 'https://twitter.com', label: 'Twitter' },
    { icon: Linkedin, href: 'https://linkedin.com', label: 'LinkedIn' },
    { icon: Instagram, href: 'https://instagram.com', label: 'Instagram' },
  ];

  const handleNav = (link) => {
    if (link.type === 'link') {
      window.scrollTo(0, 0);
      window.location.href = link.id;
      return;
    }
    const element = document.getElementById(link.id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    } else {
      window.location.href = `/#${link.id}`;
    }
  };

  return (
    <footer className="bg-dark text-white pt-16 pb-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Main Footer Content */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12"
        >
          {/* Brand Section */}
          <div className="space-y-4">
            <h3 className="font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              <img src="assets/img/logo.png" 
                alt="logo" 
                className="h-10 w-auto object-contain" 
                />
            </h3>
            <p className="text-gray-400 leading-relaxed">
              Micro Ideology Narrating Idealism - Building innovative digital solutions for modern businesses.
            </p>
            <p className="text-sm text-gray-500">
              © {currentYear} Mini App Tech. All rights reserved.
            </p>
            <div className="pt-4">
              <p className="text-xs font-bold text-gray-500 uppercase tracking-widest mb-3">Proud Member of</p>
              <motion.div
                whileHover={{ scale: 1.05 }}
                className="inline-block bg-white p-2 rounded-lg"
              >
                <img
                  src="assets/img/footer_img.png"
                  alt="BNI Member"
                  className="h-12 w-auto object-contain"
                />
              </motion.div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-bold mb-6">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link, index) => (
                <motion.li
                  key={index}
                  whileHover={{ x: 8 }}
                  className="transition-transform"
                >
                  <button
                    onClick={() => handleNav(link)}
                    className="text-gray-400 hover:text-primary transition-colors duration-200 text-sm font-medium"
                  >
                    {link.name}
                  </button>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-lg font-bold mb-6">Services</h4>
            <ul className="space-y-3">
              {[
                'Web Development',
                'Mobile Apps',
                'Custom EPR Software',
                'SMS & WhatsApp',
                'Payment Integration',
              ].map((service, index) => (
                <motion.li
                  key={index}
                  whileHover={{ x: 8 }}
                  className="transition-transform"
                >
                  <a
                    href="/contact"
                    className="text-gray-400 hover:text-primary transition-colors duration-200 text-sm font-medium"
                  >
                    {service}
                  </a>
                </motion.li>
              ))}
            </ul>
          </div>

          {/* Contact & Social */}
          <div>
            <h4 className="text-lg font-bold mb-6">Connect With Us</h4>
            <div className="space-y-4">
              <div>
                <p className="text-gray-400 text-sm mb-2">Address</p>
                <a
                  href="https://maps.google.com/?q=Ramasamy+Plaza+Madurai"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:text-secondary transition-colors duration-200 font-semibold text-sm"
                >
                  Ramasamy Plaza, Madurai
                </a>
              </div>
              <div>
                <p className="text-gray-400 text-sm mb-2">Email</p>
                <a
                  href="mailto:hr@miniapptech.in"
                  className="text-primary hover:text-secondary transition-colors duration-200 font-semibold text-sm"
                >
                  hr@miniapptech.in
                </a>
              </div>
              <div>
                <p className="text-gray-400 text-sm mb-2">Phone</p>
                <a
                  href="tel:+918807683685"
                  className="text-primary hover:text-secondary transition-colors duration-200 font-semibold text-sm"
                >
                  +91 8807683685
                </a>
              </div>
              <div className="flex gap-4 pt-2">
                {socialLinks.map((social, index) => {
                  const Icon = social.icon;
                  return (
                    <motion.a
                      key={index}
                      href={social.href}
                      whileHover={{ scale: 1.2, rotate: 10 }}
                      whileTap={{ scale: 0.95 }}
                      className="p-2 bg-gray-800 hover:bg-primary rounded-lg transition-colors duration-200"
                      aria-label={social.label}
                    >
                      <Icon size={18} />
                    </motion.a>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Divider */}
        <motion.div
          initial={{ scaleX: 0 }}
          whileInView={{ scaleX: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="h-px bg-gradient-to-r from-transparent via-primary to-transparent mb-8 origin-left"
        />

        {/* Bottom Footer */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row justify-between items-center text-gray-400 text-sm"
        >
          <p>
            Built with <span className="text-primary">❤</span> by Mini App Tech Team
          </p>
          <div className="flex gap-6 mt-4 md:mt-0">
            <a href="/privacy-policy" className="hover:text-primary transition-colors duration-200">
              Privacy Policy
            </a>
            <a href="/terms-of-service" className="hover:text-primary transition-colors duration-200">
              Terms of Service
            </a>
            <a href="/cookie-policy" className="hover:text-primary transition-colors duration-200">
              Cookie Policy
            </a>
          </div>
        </motion.div>
      </div>
    </footer>
  );
};

export default Footer;
